import { loansArchiveConfig } from '@skynet/loans-feature';
import { retainersArchiveConfig } from '@skynet/retainers-feature';
import { arbitrageConfig } from '@skynet/arbitrage-feature';
import { serversConfig } from '@skynet/servers-feature';
import { botCommandsConfig } from '@skynet/admin-bot-commands-feature';
import { botMessagesConfig } from '@skynet/bot-messages-feature';
import { tradingGroupsConfig } from '@skynet/trading-groups-feature';
import { smmConfig } from '@skynet/smm-feature';
import { dexExchangesConfig } from '@skynet/dex-exchanges-feature';
import { exchangesConfig } from '@skynet/exchanges-feature';
import { usersConfig } from '@skynet/users-feature';
import { regionsConfig } from '@skynet/regions-feature';
import { itemsConfig } from '@skynet/items-feature';
import { tenantsConfig } from '@skynet/tenants-feature';
import { rolesConfig } from '@skynet/roles-feature';
import { marketingConfig } from '@skynet/marketing-feature';
import { tokenMappingConfig } from '@skynet/token-mapping-feature';
import type { TypeNavigation } from './types';
import {
  AppstoreFilled,
  AuditOutlined,
  BarsOutlined,
  BookFilled,
  CalculatorOutlined,
  ShareAltOutlined,
  CodeFilled,
  ContactsFilled,
  ControlFilled,
  DatabaseFilled,
  FilterFilled,
  WalletFilled,
  ReadFilled,
} from '@ant-design/icons';
import type { JsonProject } from 'src/store/retainers/projects/types';
import type { JsonLoan } from 'src/store/loans/types';
import type { JsonDexVault } from 'src/store/DEX/Vaults/types';
import type { JsonTrader } from 'src/store/trading/traders/types';

// The ORDER of the actions in this array is IMPORTANT
// It defines the sorting order of the navigation items that user is authorized to.
// The order matters to get the default routes if the domain is undefined, then the first item is default
// if the domain is defined, then the first item of this domain is default
export const uiNavigationActions = [
  'read:loans:summary',
  'read:loans:overview',
  'read:loans:terms',
  'read:loans:balances',
  'read:loans:tasks',
  'read:loans:transactions',
  'read:loans:trading',
  'read:loans:bots',
  loansArchiveConfig.cerbosAction,
  'read:retainers:overview-admin',
  'read:retainers:overview',
  'read:retainers:terms-admin',
  'read:retainers:terms',
  'read:retainers:balances-admin',
  'read:retainers:balances',
  'read:retainers:tasks-admin',
  'read:retainers:tasks',
  'read:retainers:transactions',
  'read:retainers:bots',
  'read:retainers:trading',
  'read:retainers:arbitrage-info',
  'read:retainers:clients',
  retainersArchiveConfig.cerbosAction,
  'read:trading:tasks',
  'read:trading:bots',
  'read:trading:reports',
  'read:trading:dex-setup',
  'read:trading:balances',
  serversConfig.cerbosAction,
  itemsConfig.cerbosAction,
  botCommandsConfig.cerbosAction,
  botMessagesConfig.cerbosAction,
  tradingGroupsConfig.cerbosAction,
  arbitrageConfig.cerbosAction,
  marketingConfig.cerbosAction,
  'read:admin:log',
  'read:admin:sub-accounts',
  tenantsConfig.cerbosAction,
  usersConfig.cerbosAction,
  rolesConfig.cerbosAction,
  exchangesConfig.cerbosAction,
  regionsConfig.cerbosAction,
  tokenMappingConfig.cerbosAction,
  // Gates the SMM pages and their realtime socket (ws-smm-effects). Kept last
  // among admin actions so it never becomes the default admin redirect.
  smmConfig.cerbosAction,
  'read:dex:blockchains',
  dexExchangesConfig.cerbosAction,
  'read:dex:transactions',
  'read:dex:vaults',
  'read:dex:vault-overview',
  'read:dex:vault-tasks',
  'read:dex:tasks',
  'read:dex:vault-details',
  'read:dex:vault-transactions',
];

interface GetNavigationConfigParams {
  currentLoan?: JsonLoan | null;
  currentRetainer?: JsonProject | null;
  currentVault?: JsonDexVault | null;
  currentActiveKey: 'new' | number | null;
  currentExchange?: number | null;
  currentTrader?: JsonTrader | null;
}

export const getNavigationConfig = ({
  currentLoan,
  currentRetainer,
  currentVault,
  currentActiveKey,
  currentExchange,
  currentTrader,
}: GetNavigationConfigParams): TypeNavigation => {
  const loanIdParam = currentLoan ? `/${currentLoan.id}` : '';
  const retainerIdParam = currentRetainer ? `/${currentRetainer.id}` : '';
  const dexIdParam = currentVault ? `/${currentVault.id}` : ``;
  const tradingIdParam = currentActiveKey ? `/${currentActiveKey}` : '';
  const tradingBotIdParam = currentExchange ? `/${currentExchange}` : '';
  const tradingBalanceParam = currentTrader ? `/${currentTrader.id}` : '';

  return {
    admin: {
      servers: serversConfig.nav,
      items: itemsConfig.nav,
      arbitrage: arbitrageConfig.nav,
      command: botCommandsConfig.nav,
      messages: botMessagesConfig.nav,
      'telegram-groups': tradingGroupsConfig.nav,
      smm: smmConfig.nav,
      marketing: marketingConfig.nav,
      log: {
        key: `admin.log`,
        label: 'Log',
        icon: <BarsOutlined />,
        url: '/admin/log',
      },
      'sub-accounts': {
        key: `admin.sub-accounts`,
        label: 'Sub-accounts',
        icon: <ShareAltOutlined />,
        url: '/admin/sub-accounts',
      },
      exchanges: exchangesConfig.nav,
      regions: regionsConfig.nav,
      tenants: tenantsConfig.nav,
      users: usersConfig.nav,
      roles: rolesConfig.nav,
      'token-mapping': tokenMappingConfig.nav,
    },
    loans: {
      summary: {
        key: `loans.summary`,
        label: 'Summary',
        icon: <CalculatorOutlined />,
        url: '/loans/summary',
      },
      overview: {
        key: `loans.overview`,
        label: 'Overview',
        icon: <BookFilled />,
        url: `/loans/overview${loanIdParam}`,
      },
      terms: {
        key: `loans.terms`,
        label: 'Loan Terms',
        icon: <DatabaseFilled />,
        url: `/loans/terms${loanIdParam}`,
      },
      balances: {
        key: `loans.balances`,
        label: 'Balances',
        icon: <AppstoreFilled />,
        url: `/loans/balances${loanIdParam}`,
      },
      tasks: {
        key: `loans.tasks`,
        label: 'Tasks',
        icon: <AppstoreFilled />,
        url: `/loans/tasks${loanIdParam}`,
      },
      transactions: {
        key: `loans.transactions`,
        label: 'Transactions',
        icon: <WalletFilled />,
        url: `/loans/transactions${loanIdParam}`,
      },

      trading: {
        key: `loans.bots`,
        label: 'Bots',
        icon: <CodeFilled />,
        url: `/loans/bots${loanIdParam}`,
      },
      [loansArchiveConfig.section]: loansArchiveConfig.nav,
    },
    retainers: {
      'overview-admin': {
        key: `retainers.overview-admin`,
        label: 'Overview',
        icon: <BookFilled />,
        url: `/retainers/overview-admin${retainerIdParam}`,
      },
      'terms-admin': {
        key: `retainers.terms-admin`,
        label: 'Project Terms',
        icon: <DatabaseFilled />,
        url: `/retainers/terms-admin${retainerIdParam}`,
      },
      'balances-admin': {
        key: `retainers.balances-admin`,
        label: 'Balances',
        icon: <AppstoreFilled />,
        url: `/retainers/balances-admin${retainerIdParam}`,
      },
      'tasks-admin': {
        key: `retainers.tasks-admin`,
        label: 'Tasks',
        icon: <AppstoreFilled />,
        url: `/retainers/tasks-admin${retainerIdParam}`,
      },
      transactions: {
        key: `retainers.transactions`,
        label: 'Transactions',
        icon: <WalletFilled />,
        url: `/retainers/transactions${retainerIdParam}`,
      },
      trading: {
        // key: `retainers.bots`,
        key: `retainers.bots`,
        label: 'Bots',
        icon: <CodeFilled />,
        url: `/retainers/bots${retainerIdParam}`,
      },
      'arbitrage-info': {
        key: `retainers.arbitrage-info`,
        label: 'Arbitrage Info',
        icon: <AuditOutlined />,
        url: `/retainers/arbitrage-info${retainerIdParam}`,
      },
      clients: {
        key: `retainers.clients`,
        label: 'Clients',
        icon: <ContactsFilled />,
        url: `/retainers/clients${retainerIdParam}`,
      },
      [retainersArchiveConfig.section]: retainersArchiveConfig.nav,
    },
    'retainers:client': {
      overview: {
        key: 'retainers.overview',
        label: 'Overview',
        icon: <BookFilled />,
        url: `/retainers/overview${retainerIdParam}`,
      },
      terms: {
        key: `retainers.terms`,
        label: 'Project Terms',
        icon: <DatabaseFilled />,
        url: `/retainers/terms${retainerIdParam}`,
      },
      balances: {
        key: `retainers.balances`,
        label: 'Balances',
        icon: <AppstoreFilled />,
        url: `/retainers/balances${retainerIdParam}`,
      },
      tasks: {
        key: `retainers.tasks`,
        label: 'Tasks',
        icon: <AppstoreFilled />,
        url: `/retainers/tasks${retainerIdParam}`,
      },
    },
    trading: {
      dashboard: {
        key: 'trading.tasks',
        label: 'Dashboard',
        icon: <BookFilled />,
        url: `/trading/tasks${tradingIdParam}`,
      },
      balances: {
        key: `trading.balances`,
        label: 'Balances',
        icon: <AppstoreFilled />,
        url: `/trading/balances${tradingBalanceParam}`,
      },
      bots: {
        key: 'trading.bots',
        label: 'Bots',
        icon: <CodeFilled />,
        url: `/trading/bots${tradingBotIdParam}`,
      },
      reports: {
        key: 'trading.reports',
        label: 'Reports',
        icon: <ReadFilled />,
        url: `/trading/reports${tradingBalanceParam}`,
      },
      dex: {
        key: 'trading.dex-setup',
        label: 'DEX Setup',
        icon: <ControlFilled />,
        url: `/trading/dex-setup`,
      },
    },
    dex: {
      blockchains: {
        key: 'dex.blockchains',
        label: 'Blockchains',
        icon: <ControlFilled />,
        url: `/dex/blockchains`,
      },
      // `dexExchangesConfig.nav` is intentionally not listed: the DEX Exchanges page is
      // reached by URL only, as before it moved into its feature package.
      tasks: {
        key: 'dex.tasks',
        label: 'Tasks',
        icon: <AppstoreFilled />,
        url: `/dex/tasks`,
      },
      transactions: {
        key: 'dex.transactions',
        label: 'Transactions',
        icon: <WalletFilled />,
        url: `/dex/transactions`,
      },
      vaults: {
        key: 'dex.vaults',
        label: 'Vaults',
        icon: <FilterFilled />,
        url: `/dex/vaults`,
      },
      'vault-overview': {
        key: 'dex.vault-overview',
        label: 'Vault Overview',
        icon: <BookFilled />,
        url: `/dex/vault-overview${dexIdParam}`,
      },
      'vault-details': {
        key: 'dex.vault-details',
        label: 'Vault Details',
        icon: <DatabaseFilled />,
        url: `/dex/vault-details${dexIdParam}`,
      },
      'vault-tasks': {
        key: 'dex.vault-tasks',
        label: 'Vault Tasks',
        icon: <AppstoreFilled />,
        url: `/dex/vault-tasks${dexIdParam}`,
      },
      'vault-transactions': {
        key: 'dex.vault-transactions',
        label: 'Vault TX',
        icon: <WalletFilled />,
        url: `/dex/vault-transactions${dexIdParam}`,
      },
    },
  };
};
